<template>
  <TransitionRoot :show="open" as="div" @after-leave="emit('after-leave')">
    <Dialog as="div" class="relative" style="z-index: 51" :initial-focus="search" @close="handleDialogClose">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
        @after-enter="handleOpen"
      >
        <div class="fixed inset-0 bg-black/25" :inert="allowScrollBehind" />
      </TransitionChild>

      <div
        class="fixed overflow-visible w-full"
        :class="{
          'inset-y-0 right-0 max-w-[100vw] md:max-w-xl': position === 'right',
          'inset-y-0 left-0 max-w-[100vw] md:max-w-xl': position === 'left',
          'inset-y-0 inset-x-0 md:w-auto': position === 'center',
          'inset-0': position === 'full',
        }"
      >
        <div
          class="flex"
          :class="{
            'h-full': position === 'right' || position === 'left',
            'min-h-full p-4 justify-center items-center': position === 'center',
            'p-4': position === 'full',
          }"
        >
          <DialogPanel
            class="bg-neutral-white flex flex-col overflow-hidden max-w-full w-full"
            :class="[
              position !== 'full' ? 'rounded-xl shadow-xl' : '',
              { 'h-full': position === 'right' || position === 'left' },
            ]"
            style="z-index: 52"
            v-bind="$attrs"
          >
            <div
              class="flex-shrink-0 w-full px-4 md:px-8 py-3 border-b border-solid border-neutral-200 flex gap-4 items-center"
            >
              <PulseIconButton icon="LineTimes" size="large" variant="secondary" @click="$emit('close')" />
              <h3 v-if="slots.topHeader" class="text-2xl font-semibold font-geist flex-1">
                <slot name="topHeader" />
              </h3>
            </div>

            <div
              class="flex-1 min-h-0 overflow-auto child"
              :class="props.styles.contentPadding ?? 'px-4 md:px-8 py-6'"
              :style="contentHeightStyle"
            >
              <h3 v-if="slots.header" class="text-2xl font-semibold font-geist mb-6">
                <slot name="header" />
              </h3>
              <slot />
            </div>
            <div
              v-if="slots.footer"
              class="flex-shrink-0 border-t border-solid border-neutral-200 flex items-center justify-end py-4 px-4 md:px-8"
            >
              <slot name="footer" />
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script lang="ts" setup>
import { TransitionRoot, TransitionChild, DialogPanel, Dialog } from '@headlessui/vue';
import type { PropType } from 'vue';

const search = ref<HTMLInputElement | null>(null);
const emit = defineEmits(['after-leave', 'close']);
const slots = useSlots();

const props = defineProps({
  allowScrollBehind: {
    default: false,
    type: Boolean,
  },
  open: {
    default: false,
    type: Boolean,
  },
  // When true, clicking the backdrop / pressing Escape won't close the modal (only the X button will).
  // Use for multi-step forms with nested modals so a stray outside-click doesn't discard progress.
  persistent: {
    default: false,
    type: Boolean,
  },
  position: {
    default: 'center',
    type: String as PropType<'right' | 'left' | 'center' | 'full'>,
  },
  styles: {
    default: () => ({}),
    type: Object as PropType<{ contentPadding?: string }>,
  },
});

defineSlots();

// HeadlessUI Dialog fires @close on both Escape and outside-click; when persistent, ignore those and
// only let the explicit X button (which emits 'close' directly) close the modal.
function handleDialogClose() {
  if (!props.persistent) emit('close');
}

const contentHeightStyle = computed(() => {
  if (props.position === 'center') {
    return { height: 'auto', maxHeight: 'calc(100vh - 8rem)' };
  }
  if (props.position === 'full') {
    return { height: slots.footer ? 'calc(100vh - 9.50rem)' : 'calc(100vh - 6rem)' };
  }

  return {};
});

const handleOpen = () => {
  if (!props.allowScrollBehind) return;
  document.documentElement.style.overflow = 'auto';
  document.getElementById('__nuxt')?.removeAttribute('inert');
};

onMounted(() => {
  window.addEventListener('keydown', e => {
    if (e.key === 'k' && e.metaKey) {
      emit('close');
    }
  });
});
</script>
